import { addDays, differenceInCalendarDays } from 'date-fns';
import { createSelector } from 'reselect';
import { filter, find, isNull } from 'lodash';
import i18n from 'i18next';
import {
  createTravelDraft,
  updateTravelDraft,
  fetchSuggestions,
  saveContractInCache,
  fetchTravelDraftContractByToken,
  fetchMobileContractData,
} from '../../api/contractApi';
import {
  convertContractToStoreFormat,
  convertSignedContractToStoreFormat,
  mapErrorsToContractSteps,
} from '../../helpers/travel/travelContractHelpers';
import {
  SET_MOBILE_CONTRACT_DATA,
  SET_MOBILE_CONTRACT_DATA_FAIL,
  SET_MOBILE_CONTRACT_DATA_SUCCESS,
  travelCalculationReducerForContract,
} from './travelCalculation';
import { ACTION_NAME, PAYMENT_METHOD } from '../../constants';
import { bindTravelContractCalculationData, bindTravelMobileContractUserData } from '../../helpers/travel/bindData';
import { TRAVEL_GET_MOBILE_CONTRACT_PREFIX } from '../../constants/travelCalcConstants';
import { getArmenianTomorrowStartDate } from '../../helpers/dates';
import { setFormsLanguage } from './global';

const SET_SIGNER = 'efes/travelContract/SET_SIGNER';
const SET_HAS_ADD_SIGNER = 'efes/travelContract/SET_HAS_ADD_SIGNER';

const ADD_TRAVELER = 'efes/travelContract/ADD_TRAVELER';
const SAVE_TRAVELER = 'efes/travelContract/SAVE_TRAVELER';
const REMOVE_TRAVELER = 'efes/travelContract/REMOVE_TRAVELER';

const SET_TRAVEL_DETAILS = 'efes/travelContract/SET_TRAVEL_DETAILS';

const SET_PAYMENT_METHOD = 'efes/travelContract/SET_PAYMENT_METHOD';
const SET_HAS_AGREED = 'efes/travelContract/SET_HAS_AGREED';

const SAVE_DRAFT_CONTRACT = 'efes/travelContract/SAVE_DRAFT_CONTRACT';
const SAVE_DRAFT_CONTRACT_SUCCESS = 'efes/travelContract/SAVE_DRAFT_CONTRACT_SUCCESS';
const SAVE_DRAFT_CONTRACT_FAIL = 'efes/travelContract/SAVE_DRAFT_CONTRACT_FAIL';

const SAVE_TRAVEL_CONTRACT_IN_CACHE = 'efes/travelContract/SAVE_TRAVEL_CONTRACT_IN_CACHE';
const SAVE_TRAVEL_CONTRACT_IN_CACHE_SUCCESS = 'efes/travelContract/SAVE_TRAVEL_CONTRACT_IN_CACHE_SUCCESS';
const SAVE_TRAVEL_CONTRACT_IN_CACHE_FAIL = 'efes/travelContract/SAVE_TRAVEL_CONTRACT_IN_CACHE_FAIL';

const CONTINUE_FROM_DRAFT = 'efes/travelContract/CONTINUE_FROM_DRAFT';
const LOAD_SIGNED_CONTRACT = 'efes/travelContract/LOAD_SIGNED_CONTRACT';

const GET_SUGGESTIONS = 'efes/travelContract/GET_SUGGESTIONS';
const GET_SUGGESTIONS_SUCCESS = 'efes/travelContract/GET_SUGGESTIONS_SUCCESS';
const GET_SUGGESTIONS_FAIL = 'efes/travelContract/GET_SUGGESTIONS_FAIL';

const GET_DRAFT_TRAVEL_CONTRACT = 'efes/travelContract/GET_DRAFT_TRAVEL_CONTRACT';
const GET_DRAFT_TRAVEL_CONTRACT_SUCCESS = 'efes/travelContract/GET_DRAFT_TRAVEL_CONTRACT_SUCCESS';
const GET_DRAFT_TRAVEL_CONTRACT_FAIL = 'efes/travelContract/GET_DRAFT_TRAVEL_CONTRACT_FAIL';

const CLEAR_TRAVEL_CONTRACT = 'efes/travelContract/CLEAR_TRAVEL_CONTRACT';

const CLEAR_SIGNER_ERRORS = 'efes/travelContract/CLEAR_SIGNER_ERRORS';
const REMOVE_TRAVELER_ERRORS = 'efes/travelContract/REMOVE_TRAVELER_ERRORS';
const CLEAR_CALCULATOR_ERRORS = 'efes/travelContract/CLEAR_CALCULATOR_ERRORS';

const SET_PROMO_CODES = 'efes/travelContract/SET_PROMO_CODES';

const initialState = {
  contractData: {
    draftId: '',
    hasAddedSigner: true,
    signer: {},
    travelers: [],
    travelDetails: {
      age: 0,
      region: '',
      destinations: [],
      range: {
        start: getArmenianTomorrowStartDate(),
      },
      days: 0,
      selectedLimit: {},
      selectedPlan: '',
      selectedCoverages: {},
      selectedPackage: {},
      prices: {},
      policyPrices: {},
      isFetchingPrice: false,
    },
    promoCodes: [],
  },
  suggestions: [],
  hasAgreed: false,
  paymentMethod: PAYMENT_METHOD.CARD,
  isGettingPrices: false,
  loadingDraftContract: false,
  draftToken: null,
  orderId: null,
  savingContractInCache: false,
  signerBackendErrors: null,
  travelerBackendErrors: null,
  calculatorBackendErrors: null,
};

const saveTravelerReducer = (travelers, newTraveler) => travelers.map((trav) => {
  if (trav.memberId === newTraveler.memberId) {
    return newTraveler;
  }
  return trav;
});

const travelRenewalData = (data) => {
  const contractData = data;
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  if (differenceInCalendarDays(contractData.travelDetails.range.start, today) <= 0) {
    const oldStart = contractData.travelDetails.range.start;
    contractData.travelDetails.range.start = addDays(today, 1);
    contractData.travelDetails.range.end = addDays(
      contractData.travelDetails.range.end,
      differenceInCalendarDays(contractData.travelDetails.range.start, oldStart),
    );
  }

  return contractData;
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_SIGNER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          signer: action.data,
          travelDetails: {
            ...state.contractData.travelDetails,
            birthday: action.data.birthday,
          },
        },
      };

    case SET_HAS_ADD_SIGNER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          hasAddedSigner: action.data,
        },
      };
    case ADD_TRAVELER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          travelers: [...state.contractData.travelers, action.data],
        },
      };

    case SAVE_TRAVELER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          travelers: saveTravelerReducer(state.contractData.travelers, action.data),
        },
      };
    case REMOVE_TRAVELER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          travelers: state.contractData.travelers.filter(
            (t) => t.memberId !== action.data.memberId,
          ),
        },
      };

    case SET_PAYMENT_METHOD:
      return {
        ...state,
        paymentMethod: action.data,
      };
    case SET_HAS_AGREED:
      return {
        ...state,
        hasAgreed: action.data,
      };
    case SAVE_DRAFT_CONTRACT_SUCCESS:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          draftId: action.result,
        },
      };
    case SAVE_TRAVEL_CONTRACT_IN_CACHE:
      return {
        ...state,
        draftToken: null,
        orderId: null,
        savingContractInCache: true,
      };
    case SAVE_TRAVEL_CONTRACT_IN_CACHE_SUCCESS:
      return {
        ...state,
        draftToken: action.result.draftToken,
        orderId: action.result.id,
        savingContractInCache: false,
      };
    case SAVE_TRAVEL_CONTRACT_IN_CACHE_FAIL:
      return {
        ...state,
        draftToken: null,
        orderId: null,
        savingContractInCache: false,
        ...action.backendErrors,
      };
    case GET_SUGGESTIONS_SUCCESS:
      return {
        ...state,
        suggestions: action.result,
      };

    case SET_TRAVEL_DETAILS: {
      return {
        ...state,
        contractData: {
          ...state.contractData,
          travelDetails: action.data,
        },
      };
    }
    case SET_PROMO_CODES: {
      return {
        ...state,
        contractData: {
          ...state.contractData,
          promoCodes: action.data,
        },
      };
    }
    case GET_DRAFT_TRAVEL_CONTRACT:
      return {
        ...state,
        loadingDraftContract: true,
      };
    case GET_DRAFT_TRAVEL_CONTRACT_SUCCESS:
      return {
        ...state,
        contractData: { ...state.contractData, ...action.result },
        loadingDraftContract: false,
      };
    case GET_DRAFT_TRAVEL_CONTRACT_FAIL:
      return {
        ...state,
        loadingDraftContract: false,
      };

    case LOAD_SIGNED_CONTRACT:
      return {
        ...state,
        contractData: { ...state.contractData, ...travelRenewalData(action.data) },
      };

    case CONTINUE_FROM_DRAFT:
      return {
        ...state,
        contractData: { ...state.contractData, ...action.data },
      };
    case CLEAR_SIGNER_ERRORS:
      return {
        ...state,
        signerBackendErrors: null,
      };
    case REMOVE_TRAVELER_ERRORS: {
      const newErrors = { ...state.travelerBackendErrors };
      delete newErrors[action.memberId];
      return {
        ...state,
        travelerBackendErrors: newErrors,
      };
    }
    case CLEAR_CALCULATOR_ERRORS:
      return {
        ...state,
        calculatorBackendErrors: null,
      };
    case CLEAR_TRAVEL_CONTRACT:
      return {
        ...initialState,
      };

    default:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          travelDetails: travelCalculationReducerForContract(
            state.contractData.travelDetails,
            action,
          ),
        },
      };
  }
}

export const saveDraftContract = () => (dispatch, getStore) => {
  const { contractData } = getStore().travelContract;
  const { draftId } = contractData;
  return dispatch({
    actions: [
      { type: SAVE_DRAFT_CONTRACT },
      { type: SAVE_DRAFT_CONTRACT_SUCCESS },
      { type: SAVE_DRAFT_CONTRACT_FAIL },
    ],
    promise: draftId ? updateTravelDraft(contractData) : createTravelDraft(contractData),
  });
};

export function saveTravelContractInCache() {
  return (dispatch, getStore) => {
    const { contractData } = getStore().travelContract;
    dispatch({ type: SAVE_TRAVEL_CONTRACT_IN_CACHE });
    return saveContractInCache(contractData)
      .then((result) => {
        dispatch({ type: SAVE_TRAVEL_CONTRACT_IN_CACHE_SUCCESS, result });
        return result;
      })
      .catch((error) => {
        const backendErrors = mapErrorsToContractSteps(error.response.data.errors, contractData);

        let customMsg = 'contactAgent';
        if (Object.values(backendErrors).some((backendError) => backendError !== null)) {
          // eslint-disable-next-line array-callback-return,consistent-return
          const selectedError = Object.values(backendErrors).find((currentError) => {
            if (currentError !== null) {
              return currentError;
            }
          });
          // eslint-disable-next-line prefer-destructuring
          customMsg = Object.values(selectedError)[0];
        }

        dispatch({
          type: SAVE_TRAVEL_CONTRACT_IN_CACHE_FAIL,
          showError: !!(
            isNull(backendErrors.signerBackendErrors)
            && isNull(backendErrors.calculatorBackendErrors)
          ),
          customMsg,
          backendErrors,
        });
        return Promise.reject();
      });
  };
}

export const setSigner = (data) => ({ type: SET_SIGNER, data });
export const setHasAddedSigner = (data) => ({ type: SET_HAS_ADD_SIGNER, data });
export const addTraveler = (data) => ({ type: ADD_TRAVELER, data });
export const saveTraveler = (data) => ({ type: SAVE_TRAVELER, data });
export const removeTraveler = (data) => ({ type: REMOVE_TRAVELER, data });

export const setPaymentMethod = (data) => ({ type: SET_PAYMENT_METHOD, data });
export const setTravelDetails = (data) => ({ type: SET_TRAVEL_DETAILS, data });

export const setHasAgreed = (data) => ({ type: SET_HAS_AGREED, data });

export const clearTravelContract = () => ({ type: CLEAR_TRAVEL_CONTRACT });

export const getSuggestions = () => ({
  actions: [
    { type: GET_SUGGESTIONS },
    { type: GET_SUGGESTIONS_SUCCESS },
    { type: GET_SUGGESTIONS_FAIL },
  ],
  promise: fetchSuggestions(),
});

export function getDraftTravelContract(id, token) {
  return {
    actions: [
      { type: GET_DRAFT_TRAVEL_CONTRACT },
      { type: GET_DRAFT_TRAVEL_CONTRACT_SUCCESS },
      { type: GET_DRAFT_TRAVEL_CONTRACT_FAIL },
    ],
    promise: fetchTravelDraftContractByToken(id, token),
  };
}

export function getDraftTravelContractAndSaveInCache(id, token) {
  return (dispatch) => dispatch(
    getDraftTravelContract(id, token),
  ).then(() => dispatch(saveTravelContractInCache()));
}

export const continueFromDraft = (draft) => {
  const contract = convertContractToStoreFormat(draft);
  return {
    type: CONTINUE_FROM_DRAFT,
    data: contract,
  };
};

export const loadSignedContract = (contract) => {
  const convertedContract = convertSignedContractToStoreFormat(contract);
  return {
    type: LOAD_SIGNED_CONTRACT,
    data: convertedContract,
  };
};

export const selectSuggestions = createSelector(
  (state) => state.travelContract.suggestions,
  (state) => state.travelContract.contractData.signer,
  (suggestions, signer) => filter(suggestions, (s) => s.passport !== signer.passport),
);

export function setOrUpdateSigner(signerData) {
  return (dispatch, getState) => {
    const { signer, travelers } = getState().travelContract.contractData;
    const isSignerTraveling = find(travelers, ({ memberId }) => memberId === signer.memberId);
    if (isSignerTraveling) {
      dispatch(
        saveTraveler({
          ...isSignerTraveling,
          ...signerData,
        }),
      );
    }
    dispatch(setSigner(signerData));
  };
}

export const clearSignerBackendErrors = () => ({ type: CLEAR_SIGNER_ERRORS });
export const removeTravelerBackendErrors = (memberId) => ({
  type: REMOVE_TRAVELER_ERRORS,
  memberId,
});
export const clearCalculationBackendErrors = () => ({
  type: CLEAR_CALCULATOR_ERRORS,
});

export const addPromoCode = (newPromoCode) => (dispatch, getState) => {
  const currentPromoCodes = getState().travelContract.contractData.promoCodes || [];
  const { policyNumber, passport, socialCard, memberId } = newPromoCode;

  if (currentPromoCodes.some((pc) => pc.memberId === memberId)) {
    return;
  }
  dispatch({
    type: SET_PROMO_CODES,
    data: [...currentPromoCodes, { policyNumber, passport, socialCard, memberId }],
  });
};

export const setPromoCodeData = (travelers) => (dispatch, getState) => {
  const currentPromoCodes = getState().travelContract.contractData.promoCodes || [];
  const members = travelers.filter((traveler) => !!traveler && !!traveler.passport);

  const newData = members
    .map((traveler) => {
      const promoCode = currentPromoCodes.find((el) => el.memberId === traveler.memberId);
      const { memberId, passport, socCard } = traveler;
      return promoCode
        ? {
          memberId,
          passport,
          socialCard: socCard,
          policyNumber: promoCode.policyNumber,
        }
        : null;
    })
    .filter((el) => !!el);

  dispatch({
    type: SET_PROMO_CODES,
    data: newData,
  });
};

export const removePromoCode = (memberId) => (dispatch, getState) => {
  const currentPromoCodes = getState().travelContract.contractData.promoCodes;
  dispatch({
    type: SET_PROMO_CODES,
    data: currentPromoCodes.filter((pc) => pc.memberId !== memberId),
  });
};

export const getMobileContractData = (token, prefix) => (dispatch, getState) => {
  dispatch({
    actions: [
      { type: SET_MOBILE_CONTRACT_DATA, name: ACTION_NAME.CALCULATOR },
      { type: SET_MOBILE_CONTRACT_DATA_SUCCESS, name: ACTION_NAME.CALCULATOR },
      {
        type: SET_MOBILE_CONTRACT_DATA_FAIL,
        name: ACTION_NAME.CALCULATOR,
        showError: false,
      },
    ],
    promise: fetchMobileContractData({ token, prefix }, TRAVEL_GET_MOBILE_CONTRACT_PREFIX).then(
      (response) => {
        const userDetails = bindTravelMobileContractUserData(
          { ...response.userDetails, email: response.email },
        );
        i18n.changeLanguage(response.language);
        dispatch(setOrUpdateSigner({ ...userDetails, resident: 1, memberId: 0 }));
        dispatch(setHasAddedSigner(response.isTraveler));
        dispatch(setFormsLanguage(response.language));

        if (response.travelCalculatorDetails) {
          return bindTravelContractCalculationData(
            response.travelCalculatorDetails,
            getState().travelDestinations.allDestinations,
          );
        }
        return null;
      },
    ),
  });
};
